<!--

 -->

<template>
  <edit-employee-layout
    ref="editEmployeeLayout"
    :employee-id="empNumber"
    screen="salary"
    @reset-profile-attachments="resetProfileAttachmentsTable"
  >
    <div class="gsl-hrm-horizontal-padding gsl-hrm-vertical-padding">
      <profile-payroll-details :employee-id="empNumber" />
      <oxd-divider />
    </div>

    <save-salary-component
      v-if="showSaveModal"
      :http="http"
      :paygrades="paygrades"
      :pay-frequencies="payFrequencies"
      :currencies="currencies"
      :account-types="accountTypes"
      @close="onSaveModalClose"
    ></save-salary-component>
    <edit-salary-component
      v-if="showEditModal"
      :http="http"
      :data="editModalState"
      :paygrades="paygrades"
      :pay-frequencies="payFrequencies"
      :currencies="currencies"
      :account-types="accountTypes"
      @close="onEditModalClose"
    ></edit-salary-component>

    <div
      v-if="$can.update(`salary_details`)"
      class="gsl-hrm-horizontal-padding gsl-hrm-vertical-padding"
    >
      <profile-action-header
        :action-button-shown="$can.update(`salary_details`)"
        @click="onClickAdd"
      >
        {{ $t('pim.assigned_salary_components') }}
      </profile-action-header>
    </div>
    <table-header
      v-if="$can.update(`salary_details`)"
      :selected="checkedItems.length"
      :total="total"
      :loading="isLoading"
      @delete="onClickDeleteSelected"
    ></table-header>
    <div v-if="$can.update(`salary_details`)" class="gsl-hrm-container">
      <oxd-card-table
        v-model:selected="checkedItems"
        :headers="tableHeaders"
        :items="items?.data"
        :selectable="$can.delete(`salary_details`)"
        :disabled="isDisabled"
        :clickable="false"
        :loading="isLoading"
        class="gsl-hrm-salary-list"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
    <div v-if="$can.update(`salary_details`)" class="gsl-hrm-bottom-container">
      <oxd-pagination
        v-if="showPaginator"
        v-model:current="currentPage"
        :length="pages"
      />
    </div>
    <div class="gsl-hrm-horizontal-padding gsl-hrm-vertical-padding">
      <oxd-divider />
    </div>

    <employee-salary-deduction
      v-if="$can.update(`salary_details`)"
      :emp-number="empNumber"
    />
    <div class="gsl-hrm-horizontal-padding gsl-hrm-vertical-padding">
      <oxd-divider />
    </div>

    <employee-salary-other-component
      v-if="$can.update(`salary_details`)"
      :emp-number="empNumber"
    />

    <div
      class="gsl-hrm-horizontal-padding gsl-hrm-vertical-padding"
      style="text-align: right"
    >
      <oxd-button
        v-if="$can.update(`salary_details`)"
        display-type="secondary"
        :label="$t('pim.generate_payslip')"
        @click="onClickPayslipGenerator"
      />
      <oxd-divider />
    </div>
    <employee-payslip-generate-modal
      v-if="showPayslipGenerator"
      :emp-number="empNumber"
      @close="onClosePayslipGenerator"
    >
    </employee-payslip-generate-modal>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
    <oxd-alert
      type="warn"
      :show="true"
      style="margin: 10px"
      :message="`The salary slip password is a combination of your Last Name followed by your Date of Birth in the format ${(
        jsDateFormat || ''
      ).replace(/-/g, '')}. Example: Smith19170101`"
    ></oxd-alert>
  </edit-employee-layout>
</template>

<script>
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {APIService} from '@ohrm/core/util/services/api.service';
import ProfileActionHeader from '@/orangehrmPimPlugin/components/ProfileActionHeader';
import EditEmployeeLayout from '@/orangehrmPimPlugin/components/EditEmployeeLayout';
import SaveSalaryComponent from '@/orangehrmPimPlugin/components/SaveSalaryComponent';
import EditSalaryComponent from '@/orangehrmPimPlugin/components/EditSalaryComponent';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import ProfilePayrollDetails from '@/orangehrmPimPlugin/components/ProfilePayrollDetails';
import EmployeeSalaryDeduction from '@/orangehrmPimPlugin/components/EmployeeSalaryDeduction';
import EmployeeSalaryOtherComponent from '@/orangehrmPimPlugin/components/EmployeeSalaryOtherComponent';
import EmployeePaySlipGenerateModal from '@/orangehrmPimPlugin/components/EmployeePaySlipGenerateModal';
import {OxdAlert} from '@ohrm/oxd';
import useDateFormat from '@/core/util/composable/useDateFormat';

const salaryNormalizer = (data) => {
  return data.map((item) => {
    return {
      id: item.id,
      name: item.salaryName,
      amount: item.amount,
      currency: item.currencyType?.name,
      frequency: item.payPeriod?.name,
      depositAmount: item.directDebit?.amount,
    };
  });
};

export default {
  components: {
    'oxd-alert': OxdAlert,
    'profile-action-header': ProfileActionHeader,
    'edit-employee-layout': EditEmployeeLayout,
    'save-salary-component': SaveSalaryComponent,
    'edit-salary-component': EditSalaryComponent,
    'delete-confirmation': DeleteConfirmationDialog,
    'profile-payroll-details': ProfilePayrollDetails,
    'employee-salary-deduction': EmployeeSalaryDeduction,
    'employee-salary-other-component': EmployeeSalaryOtherComponent,
    'employee-payslip-generate-modal': EmployeePaySlipGenerateModal,
  },

  props: {
    empNumber: {
      type: String,
      required: true,
    },
    paygrades: {
      type: Array,
      default: () => [],
    },
    payFrequencies: {
      type: Array,
      default: () => [],
    },
    currencies: {
      type: Array,
      default: () => [],
    },
    accountTypes: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees/${props.empNumber}/salary-components`,
    );

    const {jsDateFormat} = useDateFormat();
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      normalizer: salaryNormalizer,
      toastNoRecords: false,
    });
    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      jsDateFormat,
    };
  },

  data() {
    return {
      showPayslipGenerator: false,
      headers: [
        {
          name: 'name',
          slot: 'title',
          title: this.$t('pim.salary_component'),
          style: {flex: 1},
        },
        {name: 'amount', title: this.$t('claim.amount'), style: {flex: 1}},
        {
          name: 'currency',
          title: this.$t('general.currency'),
          style: {flex: 1},
        },
        {
          name: 'frequency',
          title: this.$t('pim.pay_frequency'),
          style: {flex: 1},
        },
        {
          name: 'depositAmount',
          title: this.$t('pim.direct_deposit_amount'),
          style: {flex: 1},
        },
      ],
      checkedItems: [],
      showSaveModal: false,
      showEditModal: false,
      editModalState: null,
    };
  },

  computed: {
    isDisabled() {
      return this.showSaveModal || this.showEditModal;
    },
    tableHeaders() {
      const headerActions = {
        name: 'actions',
        slot: 'action',
        title: this.$t('general.actions'),
        style: {flex: 1},
        cellType: 'oxd-table-cell-actions',
        cellConfig: {},
      };
      if (this.$can.delete(`salary_details`)) {
        headerActions.cellConfig.delete = {
          onClick: this.onClickDelete,
          component: 'oxd-icon-button',
          props: {
            name: 'trash',
          },
        };
      }
      if (this.$can.update(`salary_details`)) {
        headerActions.cellConfig.edit = {
          onClick: this.onClickEdit,
          props: {
            name: 'pencil-fill',
          },
        };
      }
      return Object.keys(headerActions.cellConfig).length > 0
        ? this.headers.concat([headerActions])
        : this.headers;
    },
  },

  methods: {
    onClosePayslipGenerator() {
      this.showPayslipGenerator = false;
      // this.$emit('reset-profile-attachments');
      if (this.$refs.editEmployeeLayout) {
        this.$refs.editEmployeeLayout?.resetProfileAttachmentsTable();
      }
    },
    resetProfileAttachmentsTable() {
      // Call the method on the child component via ref
      if (this.$refs.editEmployeeLayout) {
        this.$refs.editEmployeeLayout.resetProfileAttachmentsTable();
      }
    },
    onClickPayslipGenerator() {
      this.showPayslipGenerator = true;
    },
    onClickDeleteSelected() {
      const ids = this.checkedItems.map((index) => {
        return this.items?.data[index].id;
      });
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems(ids);
        }
      });
    },
    onClickDelete(item) {
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems([item.id]);
        }
      });
    },
    deleteItems(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({
            ids: items,
          })
          .then(() => {
            return this.$toast.deleteSuccess();
          })
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          });
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    onClickAdd() {
      this.showEditModal = false;
      this.editModalState = null;
      this.showSaveModal = true;
    },
    onClickEdit(item) {
      this.showSaveModal = false;
      this.editModalState = item;
      this.showEditModal = true;
    },
    onSaveModalClose() {
      this.showSaveModal = false;
      this.resetDataTable();
    },
    onEditModalClose() {
      this.showEditModal = false;
      this.editModalState = null;
      this.resetDataTable();
    },
  },
};
</script>
<style lang="scss" scoped>
.gsl-hrm-salary-list {
  ::v-deep(.card-header-slot) {
    border-bottom: $oxd-input-control-border--active;
    margin-bottom: 5px;
  }

  ::v-deep(.card-body-slot) {
    display: unset;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 0.2rem;
  }
}
</style>
